import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../App';
import { getSections, updateSection, reorderSections } from '../../api/cms';

const AdminCMS = () => {
  const { token } = useAuth();
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const fetchSections = useCallback(async () => {
    try {
      const data = await getSections();
      setSections(data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const handleToggleVisibility = async (sectionId, currentVisibility) => {
    try {
      await updateSection(sectionId, { is_visible: !currentVisibility }, token);
      await fetchSections();
      showMessage('success', 'Widoczność zmieniona');
    } catch (error) {
      console.error('Toggle visibility error:', error.response?.data || error);
      showMessage('error', `Błąd: ${error.response?.data?.detail || 'Nie można zmienić widoczności'}`);
    }
  };

  const handleLayoutChange = async (sectionId, newLayout) => {
    try {
      await updateSection(sectionId, { layout: newLayout }, token);
      await fetchSections();
      showMessage('success', 'Layout zmieniony');
    } catch (error) {
      console.error('Layout change error:', error.response?.data || error);
      showMessage('error', `Błąd: ${error.response?.data?.detail || 'Nie można zmienić layoutu'}`);
    }
  };

  const handleSaveSection = async () => {
    if (!selectedSection) return;
    setSaving(true);
    try {
      await updateSection(selectedSection.section_id, {
        title: selectedSection.title,
        subtitle: selectedSection.subtitle,
        content: selectedSection.content,
        images: selectedSection.images,
        styles: selectedSection.styles,
        layout: selectedSection.layout
      }, token);
      await fetchSections();
      setIsEditing(false);
      showMessage('success', 'Sekcja zapisana pomyślnie');
    } catch (error) {
      console.error('Save section error:', error.response?.data || error);
      showMessage('error', `Błąd: ${error.response?.data?.detail || 'Nie można zapisać sekcji'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (sectionId, direction) => {
    const currentIndex = sections.findIndex(s => s.section_id === sectionId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= sections.length) return;

    const newSections = [...sections];
    [newSections[currentIndex], newSections[newIndex]] = [newSections[newIndex], newSections[currentIndex]];

    // Optimistic update
    setSections(newSections);

    const orderData = newSections.map((s, i) => ({ section_id: s.section_id, order: i + 1 }));

    try {
      await reorderSections(orderData, token);
      showMessage('success', 'Kolejność zmieniona');
    } catch (error) {
      console.error('Reorder error:', error.response?.data || error);
      // Revert on failure
      await fetchSections();
      showMessage('error', `Błąd: ${error.response?.data?.detail || 'Nie można zmienić kolejności'}`);
    }
  };

  const layoutOptions = [
    { value: 'default', label: 'Domyślny' },
    { value: 'slider', label: 'Slider' },
    { value: 'grid', label: 'Siatka' },
    { value: 'cards', label: 'Karty' },
    { value: 'accordion', label: 'Akordeon' }
  ];

  const getIcon = (sectionId) => {
    const icons = {
      hero: '🏠', problem: '❓', solution: '💡', products: '📦',
      audience: '👥', bonus: '🎁', system: '⚙️', testimonials: '⭐',
      faq: '❔', cta: '🎯'
    };
    return icons[sectionId] || '📄';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Zarządzanie Sekcjami</h1>
            <p className="text-zinc-400 mt-1">Edytuj treść, obrazy i układ strony głównej</p>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sections List */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-lg font-semibold text-white mb-4">Sekcje</h2>
            {sections.map((section, index) => (
              <div
                key={section.section_id}
                className={`bg-zinc-800/50 border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedSection?.section_id === section.section_id
                    ? 'border-emerald-500'
                    : 'border-zinc-700 hover:border-zinc-600'
                }`}
                onClick={() => { setSelectedSection(section); setIsEditing(false); }}
                data-testid={`cms-section-${section.section_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getIcon(section.section_id)}</span>
                    <div>
                      <h3 className="font-medium text-white">{section.name}</h3>
                      <p className="text-xs text-zinc-500">{section.layout}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReorder(section.section_id, 'up'); }}
                        disabled={index === 0}
                        className="text-zinc-400 hover:text-white disabled:opacity-30 text-xs"
                      >
                        ▲
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReorder(section.section_id, 'down'); }}
                        disabled={index === sections.length - 1}
                        className="text-zinc-400 hover:text-white disabled:opacity-30 text-xs"
                      >
                        ▼
                      </button>
                    </div>
                    {/* Visibility toggle */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleVisibility(section.section_id, section.is_visible); }}
                      className={`p-2 rounded transition-colors ${section.is_visible ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}
                      data-testid={`toggle-visibility-${section.section_id}`}
                    >
                      {section.is_visible ? '👁' : '🚫'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Section Editor */}
          <div className="lg:col-span-2">
            {selectedSection ? (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-white">
                    {getIcon(selectedSection.section_id)} {selectedSection.name}
                  </h2>
                  <div className="flex gap-2">
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                        data-testid="edit-section-btn"
                      >
                        Edytuj
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => setIsEditing(false)}
                          className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600"
                        >
                          Anuluj
                        </button>
                        <button
                          onClick={handleSaveSection}
                          disabled={saving}
                          className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                          data-testid="save-section-btn"
                        >
                          {saving ? 'Zapisywanie...' : 'Zapisz'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Product card size selector */}
                  {selectedSection?.section_id === 'products' && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Rozmiar kart produktów</label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'sm', label: 'Małe', desc: '4 kolumny' },
                          { value: 'md', label: 'Średnie', desc: '3 kolumny (domyślne)' },
                          { value: 'lg', label: 'Duże', desc: '2 kolumny' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            disabled={!isEditing}
                            onClick={() => isEditing && setSelectedSection({
                              ...selectedSection,
                              content: { ...(selectedSection.content || {}), card_size: opt.value }
                            })}
                            className={`p-3 rounded-lg border text-center transition-all disabled:opacity-50 ${
                              (selectedSection.content?.card_size || 'md') === opt.value
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-zinc-700 bg-zinc-900 hover:border-zinc-600'
                            }`}
                          >
                            <p className="text-white font-medium text-sm">{opt.label}</p>
                            <p className="text-zinc-500 text-xs">{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                      <p className="text-zinc-500 text-xs mt-2">Zmiana rozmiaru będzie widoczna na stronie głównej po zapisaniu.</p>
                    </div>
                  )}

                  {/* Hero image URL editor */}
                  {selectedSection?.section_id === 'hero' && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Zdjęcie Hero (URL)</label>
                      <input
                        type="text"
                        value={selectedSection.images?.hero || ''}
                        onChange={(e) => setSelectedSection({
                          ...selectedSection,
                          images: { ...(selectedSection.images || {}), hero: e.target.value }
                        })}
                        disabled={!isEditing}
                        placeholder="https://..."
                        className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50 text-sm"
                      />
                    </div>
                  )}

                  {/* System image URL editor */}
                  {selectedSection?.section_id === 'system' && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Zdjęcie Sekcji (URL)</label>
                      <input
                        type="text"
                        value={selectedSection.images?.main || ''}
                        onChange={(e) => setSelectedSection({
                          ...selectedSection,
                          images: { ...(selectedSection.images || {}), main: e.target.value }
                        })}
                        disabled={!isEditing}
                        placeholder="https://..."
                        className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50 text-sm"
                      />
                    </div>
                  )}

                  {/* Layout selector */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Layout</label>
                    <select
                      value={selectedSection.layout}
                      onChange={(e) => {
                        if (isEditing) {
                          setSelectedSection({ ...selectedSection, layout: e.target.value });
                        } else {
                          handleLayoutChange(selectedSection.section_id, e.target.value);
                        }
                      }}
                      className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      data-testid="section-layout-select"
                    >
                      {layoutOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Tytuł</label>
                    <input
                      type="text"
                      value={selectedSection.title || ''}
                      onChange={(e) => setSelectedSection({ ...selectedSection, title: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                      data-testid="section-title-input"
                    />
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Podtytuł</label>
                    <textarea
                      value={selectedSection.subtitle || ''}
                      onChange={(e) => setSelectedSection({ ...selectedSection, subtitle: e.target.value })}
                      disabled={!isEditing}
                      rows={2}
                      className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                      data-testid="section-subtitle-input"
                    />
                  </div>

                  {/* Images */}
                  {selectedSection.images && Object.keys(selectedSection.images).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Obrazy</label>
                      {Object.entries(selectedSection.images).map(([key, url]) => (
                        <div key={key} className="mb-3">
                          <label className="block text-xs text-zinc-500 mb-1">{key}</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={url}
                              onChange={(e) => setSelectedSection({
                                ...selectedSection,
                                images: { ...selectedSection.images, [key]: e.target.value }
                              })}
                              disabled={!isEditing}
                              className="flex-1 px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50 text-sm"
                            />
                            {url && (
                              <img src={url} alt={key} className="h-10 w-10 object-cover rounded" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Styles */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Style</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">Kolor akcentu</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={selectedSection.styles?.accent_color || '#10b981'}
                            onChange={(e) => setSelectedSection({
                              ...selectedSection,
                              styles: { ...selectedSection.styles, accent_color: e.target.value }
                            })}
                            disabled={!isEditing}
                            className="h-10 w-16 bg-zinc-900 border border-zinc-700 rounded cursor-pointer disabled:opacity-50"
                          />
                          <input
                            type="text"
                            value={selectedSection.styles?.accent_color || '#10b981'}
                            onChange={(e) => setSelectedSection({
                              ...selectedSection,
                              styles: { ...selectedSection.styles, accent_color: e.target.value }
                            })}
                            disabled={!isEditing}
                            className="flex-1 px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">Rozmiar tytułu</label>
                        <select
                          value={selectedSection.styles?.title_size || 'text-4xl'}
                          onChange={(e) => setSelectedSection({
                            ...selectedSection,
                            styles: { ...selectedSection.styles, title_size: e.target.value }
                          })}
                          disabled={!isEditing}
                          className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                        >
                          <option value="text-2xl">Mały</option>
                          <option value="text-3xl">Średni</option>
                          <option value="text-4xl">Duży</option>
                          <option value="text-5xl">Bardzo duży</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Content (JSON editor for advanced) */}
                  {selectedSection.content && Object.keys(selectedSection.content).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Zawartość (JSON)</label>
                      <textarea
                        value={JSON.stringify(selectedSection.content, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            setSelectedSection({ ...selectedSection, content: parsed });
                          } catch (err) {
                            // Invalid JSON, don't update
                          }
                        }}
                        disabled={!isEditing}
                        rows={10}
                        className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white font-mono text-sm focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                        data-testid="section-content-json"
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-12 text-center">
                <p className="text-zinc-400">Wybierz sekcję z listy, aby edytować</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCMS;
